import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _taggedTemplateLiteral from "@babel/runtime/helpers/esm/taggedTemplateLiteral";
var _excluded = ["value", "onChange", "onFocus", "onBlur", "placeholder", "disabled", "readOnly"];

var _templateObject, _templateObject2;

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

import React, { forwardRef, useCallback, useRef } from 'react';
import { keyframes } from '@emotion/core';
import { Input as RebassInput } from '@rebass/forms';
import { isFunction, noop } from 'ramda-extension';
import useBlockingEffect from '../hooks/useBlockingEffect';
import { isInputValueEmpty, useSuperFieldContext } from '../SuperField';
import Box from '../Box';
var placeholderHidden = {
  opacity: '0 !important'
};
var placeholderVisible = {
  opacity: 0.5
};

var mergeRefs = function mergeRefs(refs) {
  return function (value) {
    refs.filter(Boolean).forEach(function (ref) {
      return isFunction(ref) ? ref(value) : ref.current = value;
    });
  };
};

var autofill = keyframes(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n\tfrom {}\n"]))); // NOTE: make sure, that contents of `autofill` and `autofillCancel` are not
// the same. Otherwise emotion actually assings both of them same
// animation.name.

var autofillCancel = keyframes(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n\tfrom {}\n\tto: {}\n"])));

var getBorderColor = function getBorderColor(_ref) {
  var hasError = _ref.hasError,
      readOnly = _ref.readOnly,
      disabled = _ref.disabled;

  if (hasError) {
    return 'danger';
  } else if (disabled || readOnly) {
    return 'muted';
  } else {
    return 'secondary';
  }
};

var Input = /*#__PURE__*/forwardRef(function (props, ref) {
  var _useSuperFieldContext = useSuperFieldContext(),
      id = _useSuperFieldContext.id,
      isLabelShrank = _useSuperFieldContext.isLabelShrank,
      hasError = _useSuperFieldContext.hasError,
      onFill = _useSuperFieldContext.onFill,
      onEmpty = _useSuperFieldContext.onEmpty,
      onFocus = _useSuperFieldContext.onFocus,
      onBlur = _useSuperFieldContext.onBlur;

  var inputRef = useRef();
  var internalInputRef = mergeRefs([inputRef, ref]);

  var value = props.value,
      onChange = props.onChange,
      _props$onFocus = props.onFocus,
      onFocusProp = _props$onFocus === void 0 ? function () {} : _props$onFocus,
      _props$onBlur = props.onBlur,
      onBlurProp = _props$onBlur === void 0 ? function () {} : _props$onBlur,
      placeholder = props.placeholder,
      disabled = props.disabled,
      readOnly = props.readOnly,
      rest = _objectWithoutProperties(props, _excluded);

  var checkDirty = useCallback(function (value) {
    if (isInputValueEmpty(value)) {
      onEmpty();
    } else {
      onFill();
    }
  }, [onEmpty, onFill]);
  useBlockingEffect(function () {
    checkDirty(value);
  }, [checkDirty, value]);

  var handleAutoFill = function handleAutoFill(event) {
    // Provide a fake value as Chrome might not let you access it for security
    // reasons.
    checkDirty(event.animationName.indexOf(autofillCancel.name) !== -1 ? inputRef.current.value : 'x');
  };

  var handleFocus = function handleFocus(event) {
    if (disabled) {
      return;
    }

    onFocus();
    return onFocusProp(event);
  };

  var handleBlur = function handleBlur(event) {
    if (disabled) {
      return;
    }

    onBlur();
    return onBlurProp(event);
  };

  var handleChange = disabled ? noop : onChange;
  var fontSize = [2, 4];
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      position: 'relative',
      '&:before': {
        content: "\"\xA0\"",
        display: 'block',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        borderBottomStyle: 'solid',
        borderBottomWidth: '1px',
        borderBottomColor: 'muted',
        pointerEvents: 'none'
      },
      '&:hover:before': {
        borderBottomColor: disabled ? 'muted' : 'gray.2'
      },
      '&:after': {
        content: "\"\xA0\"",
        display: 'block',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        borderBottomStyle: 'solid',
        borderBottomWidth: disabled || readOnly ? '1px' : '2px',
        borderBottomColor: getBorderColor(props),
        pointerEvents: 'none',
        transition: 'transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
        transform: !isLabelShrank && !hasError ? 'scaleX(0)' : ''
      }
    }
  }, /*#__PURE__*/React.createElement(RebassInput, _extends({
    sx: _objectSpread(_objectSpread({
      color: 'inherit',
      border: 'none',
      animationDuration: '50000s',
      '&:not(:-webkit-autofill)': {
        animationName: autofillCancel,
        animationDuration: '50000s'
      },
      '&:-webkit-autofill': {
        animationName: autofill,
        animationDuration: '50000s',
        fontSize: fontSize
      },
      '&:-webkit-autofill::firstline': {
        fontSize: fontSize
      },
      WebkitTapHighlightColor: 'transparent',
      '&::placeholder': placeholder,
      '&::WebkitSearchDecoration': {
        // Remove the padding when type=search.
        WebkitAppearance: 'none'
      }
    }, !isLabelShrank ? {
      '&::placeholder': placeholderHidden
    } : {
      '&:placeholder': placeholderVisible
    }), {}, {
      // FF
      '&:invalid': {
        boxShadow: 'none'
      },
      '&:focus': {
        outline: 0
      },
      width: '100%',
      fontSize: fontSize
    }),
    px: 0,
    id: id,
    onAnimationStart: handleAutoFill,
    value: value,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    placeholder: placeholder,
    disabled: disabled,
    readOnly: readOnly,
    ref: internalInputRef
  }, rest)));
});
Input.displayName = 'Input';
process.env.NODE_ENV !== "production" ? void 0 : void 0;
export default Input;